// 定义API响应类型
interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface Employee {
  id: number;
  tenant_code: string;
  employee_id: string;
  employee_name: string;
  phone_number?: string;
  email?: string;
  department?: string;
  position?: string;
  is_active: number;
  created_at: string;
  updated_at: string;
}

export interface CreateEmployeeRequest {
  employee_id: string;
  employee_name: string;
  phone_number?: string;
  email?: string;
  department?: string;
  position?: string;
  is_active?: number;
}

export interface UpdateEmployeeRequest {
  employee_id?: string;
  employee_name?: string;
  phone_number?: string;
  email?: string;
  department?: string;
  position?: string;
  is_active?: number;
}

class EmployeeService {
  private baseUrl = '/api';

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;
    const token = localStorage.getItem('token');
    
    const defaultHeaders: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`;
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  /**
   * 获取租户下的所有员工
   */
  async getEmployees(tenantCode: string): Promise<ApiResponse<Employee[]>> {
    return this.request<Employee[]>(`/${tenantCode}/employees`);
  }

  /**
   * 根据ID获取员工信息
   */
  async getEmployeeById(tenantCode: string, id: number): Promise<ApiResponse<Employee>> {
    return this.request<Employee>(`/${tenantCode}/employees/${id}`);
  }

  /**
   * 创建新员工
   */
  async createEmployee(tenantCode: string, request: CreateEmployeeRequest): Promise<ApiResponse<{ id: number }>> {
    return this.request<{ id: number }>(`/${tenantCode}/employees`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * 更新员工信息
   */
  async updateEmployee(tenantCode: string, id: number, request: UpdateEmployeeRequest): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`/${tenantCode}/employees/${id}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  }

  /**
   * 删除员工
   */
  async deleteEmployee(tenantCode: string, id: number): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`/${tenantCode}/employees/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * 根据员工ID搜索员工
   */
  async searchEmployeesByEmployeeId(tenantCode: string, employeeId: string): Promise<ApiResponse<Employee[]>> {
    return this.request<Employee[]>(`/${tenantCode}/employees/search/employee-id/${encodeURIComponent(employeeId)}`);
  }

  /**
   * 按姓名搜索员工
   */
  async searchEmployeesByName(tenantCode: string, name: string): Promise<ApiResponse<Employee[]>> {
    return this.request<Employee[]>(`/${tenantCode}/employees/search/name/${encodeURIComponent(name)}`);
  }
}

export const employeeService = new EmployeeService();
